import {useEffect, useRef, useState} from 'react';
import {ChevronDown, ListOrdered, LoaderCircle, Plus, Trash2} from 'lucide-react';
import type {KnowledgeMap, KnowledgeNode, OutlineEntry, PDFPageLabelRule, PDFStructure} from '@reados/contracts';
import {api, errorMessage} from '../api';
import {outlineBookPageLabel, outlinePageRangeText, resolveOutlineDraft} from '../pdf-outline';

export type StructureEditorTab = 'labels' | 'outline' | 'maps';
interface StructureEditorProps {
  fileId: string;
  structure: PDFStructure;
  initialTab?: StructureEditorTab;
  onSave: (value: PDFStructure) => void;
  onClose: () => void;
}

const uid = () => crypto.randomUUID();
const titles: Record<StructureEditorTab, string> = {
  labels: '配置书本页码',
  outline: '编辑目录',
  maps: '知识地图',
};

// Copy and field order follow the corresponding iPad PDF structure editors.
// Routing belongs to the reader toolbar/sidebar; this sheet edits one kind only.
export function StructureEditor({fileId, structure, initialTab = 'labels', onSave, onClose}: StructureEditorProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState<PDFStructure>(() => structuredClone(structure));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mapId, setMapId] = useState(structure.selectedMapId || structure.maps[0]?.id || '');
  const [outlineLabels, setOutlineLabels] = useState<Record<string, string>>(() => Object.fromEntries(
    structure.outline.map(entry => [entry.id, outlineBookPageLabel(structure, entry)]),
  ));
  const selectedMap = draft.maps.find(map => map.id === mapId);
  const tab = initialTab;
  let outlinePreview: PDFStructure | null = null;
  if (tab === 'outline') {
    try { outlinePreview = {...draft, outline: resolveOutlineDraft(draft, outlineLabels)}; } catch { /* Incomplete labels remain editable. */ }
  }

  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);

  function changeRule(index: number, patch: Partial<PDFPageLabelRule>) {
    setDraft(current => ({...current, rules: current.rules.map((rule, i) => i === index ? {...rule, ...patch} : rule)}));
  }

  function changeOutline(id: string, patch: Partial<OutlineEntry>) {
    setDraft(current => ({...current, outline: current.outline.map(entry => entry.id === id ? {...entry, ...patch} : entry)}));
  }

  function changeMap(update: (map: KnowledgeMap) => KnowledgeMap) {
    setDraft(current => ({...current, maps: current.maps.map(map => map.id === mapId ? update(map) : map)}));
  }

  function changeNode(id: string, update: (node: KnowledgeNode) => KnowledgeNode) {
    changeMap(map => ({...map, nodes: map.nodes.map(node => node.id === id ? update(node) : node)}));
  }

  function addRule() {
    setDraft(current => {
      const start = Math.min(Math.max(1, ...current.rules.map(rule => rule.end + 1)), current.pageCount || 1);
      return {...current, rules: [...current.rules, {start, end: start, style: 'decimal', bookStart: 1}]};
    });
  }

  function addOutlineEntry() {
    const id = uid();
    setDraft(current => ({...current, outline: [...current.outline, {id, title: '', level: 1, pageNumber: 1}]}));
    setOutlineLabels(current => ({...current, [id]: structure.labels.find(item => item.pageNumber === 1)?.label || '1'}));
  }

  async function save() {
    setBusy(true);
    setError('');
    try {
      const patch = tab === 'labels'
        ? {rules: draft.rules.map(rule => rule.style === 'custom'
          ? {...rule, labels: rule.labels?.map(label => label.trim()).filter(Boolean)}
          : rule)}
        : tab === 'outline'
          ? {outline: resolveOutlineDraft(draft, outlineLabels)}
          : {maps: draft.maps, selectedMapId: draft.selectedMapId};
      const value = await api.saveStructure(fileId, {
        rules: draft.rules,
        outline: draft.outline,
        maps: draft.maps,
        selectedMapId: draft.selectedMapId,
        ...patch,
      });
      onSave(value);
      onClose();
    } catch (value) {
      setError(errorMessage(value));
    } finally {
      setBusy(false);
    }
  }

  const saveButton = <button className="secondary" disabled={busy} onClick={() => void save()}>
    {busy && <LoaderCircle size={15} className="spin"/>}保存
  </button>;
  const cancelButton = <button className="secondary" disabled={busy} onClick={onClose}>取消</button>;

  return <dialog ref={dialog} className={`structure-dialog structure-dialog-${tab}`} aria-label={titles[tab]}
    onCancel={event => {if (busy) event.preventDefault(); else onClose();}}>
    <header>
      {tab !== 'labels' && cancelButton}
      <h2>{titles[tab]}</h2>
      {tab !== 'labels' && saveButton}
    </header>
    <div className="structure-editor-body" aria-busy={busy}>
      {tab === 'labels' && <>
        <p className="muted">为这个 PDF 定义“书上看到的页码标签”。可以配置罗马数字、阿拉伯数字，或自定义标签，例如“封面、版权、扉页”。</p>
        {!draft.rules.length && <div className="structure-empty">
          <ListOrdered size={28} aria-hidden="true"/>
          <h3>还没有页码规则</h3>
          <p className="muted">点“新增规则”开始配置。</p>
        </div>}
        {draft.rules.map((rule, index) => <div className="edit-card page-label-rule" key={index}>
          <div className="page-label-rule-fields">
            <label>起始 PDF 页<input type="number" min={1} max={draft.pageCount} value={rule.start}
              onChange={event => changeRule(index, {start: Number(event.target.value)})}/></label>
            <label>结束 PDF 页<input type="number" min={rule.start} max={draft.pageCount} value={rule.end}
              onChange={event => changeRule(index, {end: Number(event.target.value)})}/></label>
            <label>显示方式<select value={rule.style}
              onChange={event => changeRule(index, {style: event.target.value as PDFPageLabelRule['style']})}>
              <option value="decimal">数字</option>
              <option value="roman_lower">罗马小写</option>
              <option value="roman_upper">罗马大写</option>
              <option value="custom">自定义</option>
            </select></label>
            {rule.style === 'custom'
              ? <label>自定义标签<input value={rule.labels?.join('、') || ''} placeholder="封面、版权、扉页"
                onChange={event => changeRule(index, {labels: event.target.value.split(/[,，、\n]/)})}/></label>
              : <label>起始书本页码<input inputMode={rule.style === 'decimal' ? 'numeric' : 'text'}
                value={rule.bookStart ?? 1} placeholder={rule.style === 'roman_lower' ? 'i' : rule.style === 'roman_upper' ? 'I' : '1'}
                onChange={event => changeRule(index, {bookStart: event.target.value})}/></label>}
            <button className="icon-button danger-text" aria-label="删除这条页码规则" disabled={busy}
              onClick={() => setDraft(current => ({...current, rules: current.rules.filter((_, i) => i !== index)}))}>
              <Trash2 size={16}/>
            </button>
          </div>
        </div>)}
      </>}

      {tab === 'outline' && <>
        {draft.outline.map((entry, index) => <div className="edit-card" key={entry.id}>
          <div className="row space-between">
            <strong>目录条目</strong>
            <button className="icon-button danger-text" aria-label={`删除目录 ${entry.title}`} disabled={busy}
              onClick={() => setDraft(current => ({...current, outline: current.outline.filter(item => item.id !== entry.id)}))}>
              <Trash2 size={15}/>
            </button>
          </div>
          <div className="form-grid">
            <label>标题<input aria-label={`目录标题 ${index + 1}`} value={entry.title}
              onChange={event => changeOutline(entry.id, {title: event.target.value})}/></label>
            <label>起始书本页码<input aria-label={`目录起始书本页码 ${index + 1}`} value={outlineLabels[entry.id] ?? outlineBookPageLabel(draft, entry)}
              autoCapitalize="none" autoCorrect="off"
              onChange={event => setOutlineLabels(current => ({...current, [entry.id]: event.target.value}))}/></label>
          </div>
          <p className="muted">页码范围：{outlinePreview ? outlinePageRangeText(outlinePreview, entry.id) : '填写有效的起始书本页码后显示'}</p>
          <details><summary>目录层级</summary>
            <select aria-label={`目录层级 ${index + 1}`} value={entry.level}
              onChange={event => changeOutline(entry.id, {level: Number(event.target.value)})}>
              {[1, 2, 3, 4, 5, 6].map(level => <option key={level} value={level}>{level} 级</option>)}
            </select>
          </details>
        </div>)}
        <p className="muted">填写书上看到的起始页码，例如 i、III、163 或“封面”。范围自动延伸至下一个同级或上级目录之前，最后一项延伸至全书末页。</p>
        <button className="secondary" disabled={busy} onClick={addOutlineEntry}><Plus size={15}/>添加目录条目</button>
      </>}

      {tab === 'maps' && <>
        <div className="row map-select">
          <select aria-label="选择知识地图" value={mapId} onChange={event => setMapId(event.target.value)}>
            <option value="" disabled>选择知识地图</option>
            {draft.maps.map(map => <option value={map.id} key={map.id}>{map.title || '未命名地图'}</option>)}
          </select>
          <button className="secondary" disabled={busy} onClick={() => {
            const id = uid();
            setDraft(current => ({...current, maps: [...current.maps, {id, title: '新知识地图', nodes: []}], selectedMapId: current.selectedMapId || id}));
            setMapId(id);
          }}><Plus size={15}/>新建地图</button>
        </div>
        {selectedMap && <>
          <div className="row">
            <label className="grow">地图名称<input value={selectedMap.title}
              onChange={event => changeMap(map => ({...map, title: event.target.value}))}/></label>
            <button className="icon-button danger-text" aria-label="删除当前知识地图" disabled={busy} onClick={() => {
              setDraft(current => ({...current, maps: current.maps.filter(map => map.id !== mapId), selectedMapId: current.selectedMapId === mapId ? null : current.selectedMapId}));
              setMapId('');
            }}><Trash2 size={17}/></button>
          </div>
          <label className="checkbox-label"><input type="checkbox" checked={draft.selectedMapId === mapId}
            onChange={event => setDraft(current => ({...current, selectedMapId: event.target.checked ? mapId : null}))}/>设为当前地图</label>
          {selectedMap.nodes.map(node => <details className="edit-card node-card" open key={node.id}>
            <summary><ChevronDown size={14}/><strong>{node.title || '新节点'}</strong>
              <button className="icon-button danger-text" aria-label={`删除节点 ${node.title}`} disabled={busy} onClick={event => {
                event.preventDefault();
                changeMap(map => ({...map, nodes: map.nodes.filter(item => item.id !== node.id).map(item => item.parentId === node.id ? {...item, parentId: null} : item)}));
              }}><Trash2 size={14}/></button>
            </summary>
            <div className="form-grid">
              <label>节点名称<input value={node.title} onChange={event => changeNode(node.id, item => ({...item, title: event.target.value}))}/></label>
              <label>书本页码范围<input value={node.pages} placeholder="例如 25、3–15、iii–v；留空表示不绑定"
                autoCapitalize="none" autoCorrect="off"
                onChange={event => changeNode(node.id, item => ({...item, pages: event.target.value}))}/></label>
            </div>
            <p className="muted">页码范围只用于知识地图显示和点击跳转。</p>
            <details className="node-details"><summary>节点关联</summary><div className="form-grid">
              <label>上级节点<select value={node.parentId || ''} onChange={event => changeNode(node.id, item => ({...item, parentId: event.target.value || null}))}>
                <option value="">无</option>
                {selectedMap.nodes.filter(item => item.id !== node.id).map(item => <option key={item.id} value={item.id}>{item.title || '新节点'}</option>)}
              </select></label>
              <label>关联目录<select value={node.outlineEntryId || ''} onChange={event => changeNode(node.id, item => ({...item, outlineEntryId: event.target.value || null}))}>
                <option value="">无</option>
                {draft.outline.map(entry => <option key={entry.id} value={entry.id}>{entry.title}</option>)}
              </select></label>
            </div></details>
            {node.answers?.map(answer => <div className="answer-edit" key={answer.id}>
              <div className="row">
                <input aria-label="答案标题" placeholder="答案标题" value={answer.title}
                  onChange={event => changeNode(node.id, item => ({...item, answers: item.answers?.map(a => a.id === answer.id ? {...a, title: event.target.value} : a)}))}/>
                <button className="icon-button danger-text" aria-label="删除答案" disabled={busy}
                  onClick={() => changeNode(node.id, item => ({...item, answers: item.answers?.filter(a => a.id !== answer.id)}))}><Trash2 size={14}/></button>
              </div>
              <textarea aria-label="答案内容" placeholder="答案内容" value={answer.text || ''}
                onChange={event => changeNode(node.id, item => ({...item, answers: item.answers?.map(a => a.id === answer.id ? {...a, text: event.target.value} : a)}))}/>
              {answer.path && <small className="muted">{answer.path}</small>}
            </div>)}
            <button className="text-button" disabled={busy}
              onClick={() => changeNode(node.id, item => ({...item, answers: [...(item.answers || []), {id: uid(), title: '', text: ''}]}))}>＋ 添加答案</button>
          </details>)}
          <button className="secondary" disabled={busy}
            onClick={() => changeMap(map => ({...map, nodes: [...map.nodes, {id: uid(), title: '新节点', pages: ''}]}))}>
            <Plus size={15}/>添加节点
          </button>
        </>}
      </>}
    </div>
    {error && <div className="form-error" role="alert">{error}</div>}
    {tab === 'labels' && <footer>
      <button className="secondary" disabled={busy} onClick={addRule}><Plus size={15}/>新增规则</button>
      <div className="row">{cancelButton}{saveButton}</div>
    </footer>}
  </dialog>;
}

